import type {
    homeFeedQueriesInspectionQuery as InspectionQuery,
} from '../relay/__generated__/homeFeedQueriesInspectionQuery.graphql';

/**
 * Plain view models for the Inference and Widening offer sections of the Home feed panel (PRD
 * pv-platform#156). Enum-valued fields stay strings so an unknown future value shows as given.
 */
export interface OfferCountdownView {
    lastDecisionUtc: string | null;
    lastDecisionKey: string | null;
    /** YES / NO / SWIPE. */
    lastDecisionKind: string | null;
    /** The latest decision was about a Widening offer: it shares the sheet slot. */
    lastDecisionIsWidening: boolean;
    cooldownHomes: number;
    homesSinceLastDecision: number;
    moreHomes: number;
    offerableNow: boolean;
}

export interface OfferRejectionView {
    key: string;
    rejectedUtc: string;
    mayReturnUtc: string;
    stillQuiet: boolean;
}

export interface OfferDismissalView {
    key: string;
    dismissedUtc: string;
}

export interface OfferConfirmationView {
    key: string;
    confirmedUtc: string;
}

export interface OfferCandidateView {
    key: string;
    axis: string | null;
    suggestion: string;
    evidence: string;
    status: string;
}

export interface OfferHistoryView {
    rejections: readonly OfferRejectionView[];
    dismissals: readonly OfferDismissalView[];
    confirmations: readonly OfferConfirmationView[];
}

export interface InferenceOnOfferView {
    key: string;
    suggestion: string;
    evidence: string;
    skippedEvidence: string | null;
    matching: number;
    interested: number;
    homes: readonly { indexedListingId: string; address: string; matches: boolean }[];
}

export interface HomeFeedInferencesView extends OfferHistoryView {
    outcome: string;
    interestedHomes: number;
    engagedHomes: number;
    minInterestedHomes: number;
    countdown: OfferCountdownView;
    onOffer: InferenceOnOfferView | null;
    candidates: readonly OfferCandidateView[];
}

export interface WideningOnOfferView {
    key: string;
    axis: string;
    suggestion: string;
    evidence: string;
    interested: number;
    shown: number;
    homes: readonly { indexedListingId: string; address: string }[];
}

export interface WideningAxisView {
    axis: string;
    status: string;
    interested: number;
    skipped: number;
}

/**
 * A widening confirmed and awaiting agreement. ⛔ `wideningKey` is set only for PARTNER (the inspected
 * member stated it); for THIS_MEMBER and ANOTHER_MEMBER the mapper drops it, and no member id is ever
 * carried, so the panel cannot show whose statement it was.
 */
export interface PendingWideningView {
    waitingOn: string;
    wideningKey: string | null;
    kind: string;
    subject: string;
    value: string;
    statedUtc: string;
}

export interface HomeFeedWideningView extends OfferHistoryView {
    outcome: string;
    holdingInferenceKey: string | null;
    interestedWildcards: number;
    minInterestedWildcards: number;
    /** Null when the feed's gate stops the offer (UNSCORED, RENTING, NOT_COVERED). */
    countdown: OfferCountdownView | null;
    onOffer: WideningOnOfferView | null;
    axes: readonly WideningAxisView[];
    candidates: readonly OfferCandidateView[];
    pendingAgreements: readonly PendingWideningView[];
}

type Payload = InspectionQuery['response']['homeFeedInspection'];
type InferencesPayload = NonNullable<Payload['inferences']>;
type WideningPayload = NonNullable<Payload['widening']>;

function mapCountdown(c: InferencesPayload['countdown']): OfferCountdownView {
    return {
        lastDecisionUtc: c.lastDecisionUtc ?? null,
        lastDecisionKey: c.lastDecisionKey ?? null,
        lastDecisionKind: c.lastDecisionKind ?? null,
        lastDecisionIsWidening: c.lastDecisionIsWidening,
        cooldownHomes: c.cooldownHomes,
        homesSinceLastDecision: c.homesSinceLastDecision,
        moreHomes: c.moreHomes,
        offerableNow: c.offerableNow,
    };
}

function mapHistory(h: Pick<InferencesPayload, 'rejections' | 'dismissals' | 'confirmations'>): OfferHistoryView {
    return {
        rejections: h.rejections.map((r) => ({
            key: r.key,
            rejectedUtc: r.rejectedUtc,
            mayReturnUtc: r.mayReturnUtc,
            stillQuiet: r.stillQuiet,
        })),
        dismissals: h.dismissals.map((d) => ({ key: d.key, dismissedUtc: d.dismissedUtc })),
        confirmations: h.confirmations.map((c) => ({ key: c.key, confirmedUtc: c.confirmedUtc })),
    };
}

export function mapInferences(i: Payload['inferences']): HomeFeedInferencesView | null {
    if (!i) return null;
    return {
        outcome: i.outcome,
        interestedHomes: i.interestedHomes,
        engagedHomes: i.engagedHomes,
        minInterestedHomes: i.minInterestedHomes,
        countdown: mapCountdown(i.countdown),
        onOffer: i.onOffer
            ? {
                  key: i.onOffer.key,
                  suggestion: i.onOffer.suggestion,
                  evidence: i.onOffer.evidence,
                  skippedEvidence: i.onOffer.skippedEvidence ?? null,
                  matching: i.onOffer.matching,
                  interested: i.onOffer.interested,
                  homes: i.onOffer.homes.map((h) => ({
                      indexedListingId: h.indexedListingId,
                      address: h.address,
                      matches: h.matches,
                  })),
              }
            : null,
        candidates: i.candidates.map((c) => ({
            key: c.key,
            axis: null,
            suggestion: c.suggestion,
            evidence: c.evidence,
            status: c.status,
        })),
        ...mapHistory(i),
    };
}

export function mapWidening(w: Payload['widening']): HomeFeedWideningView | null {
    if (!w) return null;
    return {
        outcome: w.outcome,
        holdingInferenceKey: w.holdingInferenceKey ?? null,
        interestedWildcards: w.interestedWildcards,
        minInterestedWildcards: w.minInterestedWildcards,
        countdown: w.countdown ? mapCountdown(w.countdown) : null,
        onOffer: w.onOffer
            ? {
                  key: w.onOffer.key,
                  axis: w.onOffer.axis,
                  suggestion: w.onOffer.suggestion,
                  evidence: w.onOffer.evidence,
                  interested: w.onOffer.interested,
                  shown: w.onOffer.shown,
                  homes: w.onOffer.homes.map((h) => ({
                      indexedListingId: h.indexedListingId,
                      address: h.address,
                  })),
              }
            : null,
        axes: w.axes.map((a) => ({ axis: a.axis, status: a.status, interested: a.interested, skipped: a.skipped })),
        candidates: w.candidates.map((c) => ({
            key: c.key,
            axis: c.axis,
            suggestion: c.suggestion,
            evidence: c.evidence,
            status: c.status,
        })),
        pendingAgreements: mapPending(w.pendingAgreements),
        ...mapHistory(w),
    };
}

function mapPending(pending: WideningPayload['pendingAgreements']): PendingWideningView[] {
    return pending.map((p) => ({
        waitingOn: p.waitingOn,
        // ⛔ Only a widening the inspected member stated may name its key.
        wideningKey: p.waitingOn === 'PARTNER' ? (p.wideningKey ?? null) : null,
        kind: p.kind,
        subject: p.subject,
        value: p.value,
        statedUtc: p.statedUtc,
    }));
}
